import { useState } from 'react';
import { useT } from '@hana/i18n';
import { Segmented } from '@hana/ui';
import { KPI_PEOPLE, KPI_POS, KPI_VOL, MET, f1, fi, metHint, volBase } from './kpiPeople';
import './i18n';
import s from './kpiDept.module.css';

// Mint-KPI · tab Theo phòng (code board: tab === 'dept'). Chất lượng = điểm theo chỉ số; Khối lượng = đếm sản lượng. Không tiền.

type Grade = 'A' | 'B' | 'C' | 'D';
const GRADE_FILL: Record<Grade, string> = { A: 'var(--ok)', B: 'var(--d-blue)', C: 'var(--d-amber)', D: 'var(--err)' };
const GRADE_CHIP: Record<Grade, [string, string]> = { A: ['var(--ok-bg)', 'var(--ok)'], B: ['var(--pri-bg)', 'var(--link)'], C: ['var(--warn-bg)', 'var(--warn)'], D: ['var(--err-bg)', 'var(--err)'] };
const heat = (sc: number) => (sc >= 90 ? 'var(--ok-bg)' : sc >= 75 ? 'var(--pri-bg)' : sc >= 60 ? 'var(--warn-bg)' : 'var(--err-bg)');
const BLUE = 'var(--d-blue)';

type Bar = { u: string; seat: string; n: number; color: string; v: string; v2?: string };

function Bars({ title, sub, rows, max, cols }: { title: string; sub: string; rows: Bar[]; max: number; cols: number }) {
  const per = Math.ceil(rows.length / cols);
  return (
    <section className={s.panel} aria-label={title}>
      <div className={s.hd}><h2>{title}</h2><span>{sub}</span></div>
      <div className={s.bars} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }} role="img" aria-label={`${title}: ${rows.map((r) => `${r.u} ${r.v}`).join(', ')}`}>
        {rows.map((r, i) => (
          <div key={r.u} className={`${s.brow} ${i % per ? s.top : ''}`}>
            <div className={s.bl}><b>{r.u}</b><small>[{r.seat}]</small></div>
            <div className={s.track}><div className={s.fill} style={{ width: `${Math.max(2, (r.n / max) * 100).toFixed(1)}%`, background: r.color }} /></div>
            <b className={s.bv}>{r.v}{r.v2 ? <small>{r.v2}</small> : null}</b>
          </div>
        ))}
      </div>
    </section>
  );
}

export function useDeptSegs() {
  const t = useT();
  const [pos, setPos] = useState('BOOKING');
  const [view, setView] = useState<'q' | 'v'>('q');
  const segs = (
    <div className={s.segs}>
      <Segmented kind="toggle" label={t('hr.kd.pos')} value={pos} onChange={setPos} items={KPI_POS.map((p) => ({ key: p.key, label: p.label }))} />
      <Segmented kind="toggle" label={t('hr.kd.view')} value={view} onChange={setView} items={[{ key: 'q', label: t('hr.kd.quality') }, { key: 'v', label: t('hr.kd.volume') }]} />
    </div>
  );
  return { pos, view, segs };
}

export function HrKpiDept({ pos: posKey, view, onNote }: { pos: string; view: 'q' | 'v'; onNote: (m: string) => void }) {
  const t = useT();
  const pos = KPI_POS.find((p) => p.key === posKey) ?? KPI_POS[0]!;
  const ps = KPI_PEOPLE.filter((p) => p.dept === pos.key);
  const scored = ps.filter((p) => p.fin != null);

  if (view === 'q') {
    const avg = scored.reduce((a, p) => a + (p.fin ?? 0), 0) / (scored.length || 1);
    const mgrTag = pos.key === 'SALE' ? '[SALE-MGR]' : pos.mgr.split(' ')[0];
    const mgrSub = pos.mgr.split(' ').slice(1).join(' ');
    const rank = [...scored].sort((a, b) => (b.fin ?? 0) - (a.fin ?? 0));
    const w = Math.floor(55 / pos.mets.length);
    return (
      <div className={s.grid}>
        <section className={s.tiles} aria-label={t('hr.kd.metrics')}>
          <div className={s.tile}><span>{t('hr.kd.staff')}</span><b>{fi(ps.length)}</b><span>{pos.seats}</span></div>
          <div className={s.tile}><span>{t('hr.kd.avgDept')}</span><b>{f1(avg)}</b></div>
          <div className={s.tile}><span>{t('hr.kd.confirmAdj')}</span><b>{mgrTag}</b><span>{mgrSub}</span></div>
          <div className={s.tile}><span>{t('hr.kd.activeMetrics')}</span><b>{fi(pos.mets.length)}</b><span>{t('hr.kd.catalog', { k: pos.key })}</span></div>
        </section>
        {rank.length ? (
          <Bars title={t('hr.kd.rankTitle', { p: pos.label })} sub={t('hr.kd.rankSub')} max={100} cols={rank.length > 4 ? 2 : 1}
            rows={rank.map((p) => ({ u: p.u, seat: p.seat, n: p.fin ?? 0, color: GRADE_FILL[p.grade as Grade], v: f1(p.fin ?? 0), v2: t('hr.kd.gradeN', { g: p.grade }) }))} />
        ) : null}
        <section className={s.panel} aria-label={t('hr.kd.byMetric', { p: pos.label })}>
          <div className={s.hd}><h2>{t('hr.kd.byMetric', { p: pos.label })}</h2><span>{t('hr.kd.heatNote')}</span></div>
          <table className={s.tbl} aria-label={t('hr.kd.byMetric', { p: pos.label })}>
            <colgroup>
              <col style={{ width: '14%' }} /><col style={{ width: '11%' }} />
              {pos.mets.map((m) => <col key={m} style={{ width: `${w}%` }} />)}
              <col style={{ width: '10%' }} /><col style={{ width: '10%' }} />
            </colgroup>
            <thead><tr>
              <th scope="col">{t('hr.kd.person')}</th><th scope="col">{t('hr.kd.seat')}</th>
              {pos.mets.map((m) => <th key={m} scope="col" className={s.r}>{m}</th>)}
              <th scope="col" className={s.r}>{t('hr.kd.score')}</th><th scope="col" className={s.r}>{t('hr.kd.grade')}</th>
            </tr></thead>
            <tbody>
              {ps.map((p) => (
                <tr key={p.u}>
                  <td className={s.nm}>{p.u}</td><td className={s.seat}>{p.seat}</td>
                  {pos.mets.map((m) => {
                    const x = p.mets.find((y) => y.m === m);
                    return x ? <td key={m} className={`${s.r} ${s.sc}`} style={{ background: heat(x.sc) }}>{fi(x.sc)}</td> : <td key={m} className={`${s.r} ${s.none}`}>—</td>;
                  })}
                  <td className={`${s.r} ${s.sc}`}>{p.fin == null ? '—' : f1(p.fin)}</td>
                  <td className={s.r}>{p.fin == null ? <span className={s.none}>{t('hr.kd.noSet')}</span> : <span className={s.chipG} style={{ background: GRADE_CHIP[p.grade as Grade][0], color: GRADE_CHIP[p.grade as Grade][1] }}>{p.grade}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className={s.panel} aria-label={t('hr.kd.metricNames')}>
          <div className={s.hd}><h2>{t('hr.kd.metricNames')}</h2></div>
          <div className={s.list}>
            {pos.mets.map((m) => <div key={m} className={s.lrow}><span>{m}</span><span>{metHint(m)}</span><b>{MET[m]?.name}</b></div>)}
          </div>
        </section>
      </div>
    );
  }

  const vol = KPI_VOL[pos.key] ?? [];
  const ps2 = ps.filter((p) => !p.noSet);
  const tot = vol.map(([, f]) => ps2.reduce((a, p) => a + Math.round(volBase(pos.key, p.u) * f), 0));
  const rows = ps2.map((p) => ({ u: p.u, seat: p.seat, n: Math.round(volBase(pos.key, p.u) * (vol[0]?.[1] ?? 1)), color: BLUE, v: fi(Math.round(volBase(pos.key, p.u) * (vol[0]?.[1] ?? 1))) })).sort((a, b) => b.n - a.n);
  const mx = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div className={s.grid}>
      <section className={s.tiles} aria-label={t('hr.kd.metrics')}>
        {vol.map(([k], i) => <div key={k} className={s.tile}><span>{k}</span><b>{fi(tot[i] ?? 0)}</b><span>{t('hr.kd.wholePos')}</span></div>)}
      </section>
      {rows.length ? <Bars title={t('hr.kd.volByPerson', { m: vol[0]?.[0] ?? '', p: pos.label })} sub={t('hr.kd.mainUnit')} rows={rows} max={mx} cols={rows.length > 4 ? 2 : 1} /> : null}
      <section className={s.panel} aria-label={t('hr.kd.volTitle', { p: pos.label })}>
        <div className={s.hd}>
          <h2>{t('hr.kd.volTitle', { p: pos.label })}</h2>
          <span>{t('hr.kd.countFrom')}{pos.key === 'CS' ? t('hr.kd.csPair') : ''}</span>
          <button type="button" onClick={() => onNote(t('hr.kd.noWrite'))}>{t('hr.kd.loadHistory')}</button>
        </div>
        <table className={s.tbl} aria-label={t('hr.kd.volTitle', { p: pos.label })}>
          <colgroup><col style={{ width: '18%' }} /><col style={{ width: '14%' }} />{vol.map(([k]) => <col key={k} style={{ width: '17%' }} />)}</colgroup>
          <thead><tr><th scope="col">{t('hr.kd.person')}</th><th scope="col">{t('hr.kd.seat')}</th>{vol.map(([k]) => <th key={k} scope="col" className={s.r}>{k}</th>)}</tr></thead>
          <tbody>
            {ps2.map((p) => (
              <tr key={p.u}>
                <td className={s.nm}>{p.u}</td><td className={s.seat}>{p.seat}</td>
                {vol.map(([k, f]) => <td key={k} className={s.r}>{fi(Math.round(volBase(pos.key, p.u) * f))}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
